import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { SUPPORTED_LANGS } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=1600&q=80"; // Salinas Grandes / salt flats aerial
// Lithium: aerial view of turquoise mineral-rich water — evokes salares of NW Argentina
const LITHIUM_IMG =
  "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=1400&q=80";
const LITHIUM_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&w=1400&q=80";
// Oil & gas: industrial hydrocarbon processing plant — emblematic of Vaca Muerta operations
const OIL_IMG =
  "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1400&q=80";
const OIL_IMG_FALLBACK =
  "https://images.pexels.com/photos/162568/oil-pump-jack-sunset-clouds-silhouette-162568.jpeg?auto=compress&cs=tinysrgb&w=1400";
const DASHBOARD_IMG =
  "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=1400"; // analytical dashboard / financial charts
const DASHBOARD_IMG_FALLBACK =
  "https://images.pexels.com/photos/590041/pexels-photo-590041.jpeg?auto=compress&cs=tinysrgb&w=1400";
// Infrastructure strip: energy/mining infrastructure at scale
const INFRA_IMG =
  "https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1600&q=80";
const INFRA_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1516937941344-00b4e0337589?auto=format&fit=crop&w=1600&q=80";

// Reliable image loader: swaps to a fallback URL on error.
function SmartImg({ src, fallback, alt, ...rest }) {
  const [current, setCurrent] = React.useState(src);
  React.useEffect(() => {
    setCurrent(src);
  }, [src]);
  return (
    <img
      src={current}
      alt={alt}
      onError={() => {
        if (fallback && current !== fallback) setCurrent(fallback);
      }}
      {...rest}
    />
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const { lang: langParam } = useParams();
  const lang = SUPPORTED_LANGS.includes(langParam) ? langParam : "ar";
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("contact.errorRequired");
    if (!form.email.trim()) e.email = t("contact.errorRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t("contact.errorEmail");
    if (!form.message.trim()) e.message = t("contact.errorRequired");
    return e;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      navigate("/thank_you");
    }, 350);
  };

  return (
    <>
      <PageHead titleKey="meta.home.title" descriptionKey="meta.home.description" />

      {/* Hero */}
      <section className="ag-hero">
        <div className="ag-hero__bg" aria-hidden="true">
          <img src={HERO_IMG} alt="" loading="eager" />
          <div className="ag-hero__scrim" />
        </div>
        <div className="ag-container ag-hero__inner">
          <span className="ag-eyebrow ag-eyebrow--onDark">{t("hero.badge")}</span>
          <h1 className="ag-hero__title">{t("hero.title")}</h1>
          <p className="ag-hero__subtitle">{t("hero.subtitle")}</p>
          <p className="ag-hero__lede">{t("hero.lede")}</p>
          <div className="ag-hero__ctas">
            <a href={`#sectors`} className="ag-btn ag-btn--primary">
              {t("hero.ctaPrimary")}
            </a>
            <a href={`#contact`} className="ag-btn ag-btn--onDark">
              {t("nav.ctaContact")}
            </a>
          </div>

          <ul className="ag-hero__metrics">
            <li>
              <span className="ag-hero__mLabel">{t("hero.metricLithium")}</span>
              <span className="ag-hero__mValue">{t("hero.metricLithiumValue")}</span>
            </li>
            <li>
              <span className="ag-hero__mLabel">{t("hero.metricOil")}</span>
              <span className="ag-hero__mValue">{t("hero.metricOilValue")}</span>
            </li>
            <li>
              <span className="ag-hero__mLabel">{t("hero.metricGas")}</span>
              <span className="ag-hero__mValue">{t("hero.metricGasValue")}</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Intro */}
      <section className="ag-section" id="analysis">
        <div className="ag-container ag-intro">
          <div>
            <span className="ag-eyebrow">{t("intro.eyebrow")}</span>
            <h2 className="ag-h2">{t("intro.title")}</h2>
          </div>
          <div className="ag-intro__copy">
            <p>{t("intro.p1")}</p>
            <p>{t("intro.p2")}</p>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="ag-section ag-section--muted">
        <div className="ag-container">
          <div className="ag-sectionHead">
            <span className="ag-eyebrow">{t("why.eyebrow")}</span>
            <h2 className="ag-h2">{t("why.title")}</h2>
          </div>
          <ul className="ag-why__grid">
            {[1, 2, 3, 4].map((i) => (
              <li className="ag-why__card" key={i}>
                <span className="ag-why__num" aria-hidden="true">
                  0{i}
                </span>
                <h3>{t(`why.item${i}Title`)}</h3>
                <p>{t(`why.item${i}Desc`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sectors */}
      <section className="ag-section" id="sectors">
        <div className="ag-container">
          <div className="ag-sectionHead">
            <span className="ag-eyebrow">{t("sectors.eyebrow")}</span>
            <h2 className="ag-h2">{t("sectors.title")}</h2>
          </div>

          <div className="ag-sectors">
            <article className="ag-sector">
              <div className="ag-sector__media">
                <SmartImg src={LITHIUM_IMG} fallback={LITHIUM_IMG_FALLBACK} alt="Salinas Grandes — salares del noroeste argentino" loading="lazy" />
              </div>
              <div className="ag-sector__body">
                <span className="ag-tag ag-tag--terracotta">{t("sectors.lithiumTag")}</span>
                <h3 className="ag-h3">{t("sectors.lithiumTitle")}</h3>
                <p>{t("sectors.lithiumDesc")}</p>
                <ul className="ag-sector__stats">
                  <li>
                    <span>{t("sectors.lithiumStat1")}</span>
                    <strong>{t("sectors.lithiumStat1Value")}</strong>
                  </li>
                  <li>
                    <span>{t("sectors.lithiumStat2")}</span>
                    <strong>{t("sectors.lithiumStat2Value")}</strong>
                  </li>
                </ul>
                <a className="ag-link" href={`#contact`}>
                  {t("sectors.exploreCta")} →
                </a>
              </div>
            </article>

            <article className="ag-sector ag-sector--reverse">
              <div className="ag-sector__media">
                <SmartImg src={OIL_IMG} fallback={OIL_IMG_FALLBACK} alt="Torre de bombeo en Vaca Muerta, Neuquén" loading="lazy" />
              </div>
              <div className="ag-sector__body">
                <span className="ag-tag ag-tag--navy">{t("sectors.oilTag")}</span>
                <h3 className="ag-h3">{t("sectors.oilTitle")}</h3>
                <p>{t("sectors.oilDesc")}</p>
                <ul className="ag-sector__stats">
                  <li>
                    <span>{t("sectors.oilStat1")}</span>
                    <strong>{t("sectors.oilStat1Value")}</strong>
                  </li>
                  <li>
                    <span>{t("sectors.oilStat2")}</span>
                    <strong>{t("sectors.oilStat2Value")}</strong>
                  </li>
                </ul>
                <a className="ag-link" href={`#contact`}>
                  {t("sectors.exploreCta")} →
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* How */}
      <section className="ag-section ag-section--dark">
        <div className="ag-container">
          <div className="ag-sectionHead ag-sectionHead--onDark">
            <span className="ag-eyebrow ag-eyebrow--onDark">{t("how.eyebrow")}</span>
            <h2 className="ag-h2">{t("how.title")}</h2>
            <p className="ag-lede">{t("how.lede")}</p>
          </div>
          <ol className="ag-how__grid">
            {[1, 2, 3, 4].map((i) => (
              <li className="ag-how__step" key={i}>
                <span className="ag-how__num">{t(`how.step${i}Label`)}</span>
                <h3>{t(`how.step${i}Title`)}</h3>
                <p>{t(`how.step${i}Desc`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Transparency */}
      <section className="ag-section">
        <div className="ag-container ag-transparency">
          <div className="ag-transparency__media">
            <SmartImg src={DASHBOARD_IMG} fallback={DASHBOARD_IMG_FALLBACK} alt="Panel analítico con indicadores macroeconómicos" loading="lazy" />
          </div>
          <div className="ag-transparency__body">
            <span className="ag-eyebrow">{t("transparency.eyebrow")}</span>
            <h2 className="ag-h2">{t("transparency.title")}</h2>
            <p>{t("transparency.p1")}</p>
            <p>{t("transparency.p2")}</p>
            <ul className="ag-badges">
              <li>{t("transparency.badge1")}</li>
              <li>{t("transparency.badge2")}</li>
              <li>{t("transparency.badge3")}</li>
              <li>{t("transparency.badge4")}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="ag-section ag-section--muted">
        <div className="ag-container">
          <div className="ag-sectionHead">
            <span className="ag-eyebrow">{t("stats.eyebrow")}</span>
            <h2 className="ag-h2">{t("stats.title")}</h2>
          </div>
          <ul className="ag-stats">
            {[1, 2, 3, 4].map((i) => (
              <li key={i}>
                <strong>{t(`stats.s${i}Value`)}</strong>
                <span>{t(`stats.s${i}Label`)}</span>
              </li>
            ))}
          </ul>
          <div className="ag-infraStrip">
            <SmartImg src={INFRA_IMG} fallback={INFRA_IMG_FALLBACK} alt="Infraestructura industrial en Neuquén" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="ag-section ag-section--contact" id="contact">
        <div className="ag-container ag-contact">
          <div className="ag-contact__head">
            <span className="ag-eyebrow">{t("contact.eyebrow")}</span>
            <h2 className="ag-h2">{t("contact.title")}</h2>
            <p>{t("contact.subtitle")}</p>
          </div>
          <form className="ag-form" onSubmit={onSubmit} noValidate>
            <label className="ag-field">
              <span className="ag-field__label">{t("contact.nameLabel")}</span>
              <input
                type="text"
                className="ag-field__input"
                placeholder={t("contact.namePlaceholder")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={!!errors.name}
              />
              {errors.name && <span className="ag-field__error">{errors.name}</span>}
            </label>
            <label className="ag-field">
              <span className="ag-field__label">{t("contact.emailLabel")}</span>
              <input
                type="email"
                className="ag-field__input"
                placeholder={t("contact.emailPlaceholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                aria-invalid={!!errors.email}
              />
              {errors.email && <span className="ag-field__error">{errors.email}</span>}
            </label>
            <label className="ag-field ag-field--full">
              <span className="ag-field__label">{t("contact.messageLabel")}</span>
              <textarea
                className="ag-field__input ag-field__textarea"
                rows={5}
                placeholder={t("contact.messagePlaceholder")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                aria-invalid={!!errors.message}
              />
              {errors.message && <span className="ag-field__error">{errors.message}</span>}
            </label>
            <p className="ag-form__note">{t("contact.consentNote")}</p>
            <button type="submit" className="ag-btn ag-btn--primary ag-btn--lg" disabled={submitting}>
              {submitting ? t("contact.submitting") : t("contact.submit")}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
